import { GetServerSideProps, NextPage } from 'next';
import PageLayout, { MenuProps } from '../../../layouts/Page';
import { GET_FORM } from '../../../queries/form';
import { Form } from '../../../types/schema';
import { initializeApollo } from '../../../utils/apollo';
import { Error, sendError } from '../../../utils/error';
import logging from '../../../utils/logging';
import { flatten } from '../../../utils/object';
import FormView from '../../../views/FormView';

type FormViewProps = {
  form: Form & { id?: number };
  menus: MenuProps;
  locale: string;
  additionalInfo?: Record<string, string>;
};

/**
 * FormView NextPage
 */
const FormViewPage: NextPage<FormViewProps> = ({ form, menus, additionalInfo }) => {
  return (
    <PageLayout menus={menus} locale={form.locale as string} isHeaderTransparent={true}>
      <FormView form={form} additionalInfo={additionalInfo} />
    </PageLayout>
  );
};

/**
 * GetServerSideProps
 */
export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const slug = ctx.params?.slug as string;

  if (!slug) {
    logging.info('No slug provided on form route');

    return {
      notFound: true,
    };
  }

  const apolloClient = initializeApollo();

  try {
    const { data } = await apolloClient.query({
      query: GET_FORM,
      variables: {
        slug,
      },
    });

    const newData = flatten(data);

    if (!newData.forms || (newData.forms as Form[]).length === 0)
      return {
        notFound: true,
      };

    const addParams = Object.entries(ctx.query as Record<string, string>)
      .filter(([key]) => key.startsWith('add_'))
      .reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key.slice(4)] = value;
        return acc;
      }, {});
    const additionalInfo = Object.keys(addParams).length > 0 ? addParams : undefined;

    return {
      props: {
        form: (newData as { forms: Form[] }).forms[0],
        menus: {
          header: newData.header,
          technologies: newData.technologies,
          services: newData.services,
          footerPt: newData.footerPt,
          footerEn: newData.footerEn,
        },
        ...(additionalInfo && { additionalInfo }),
      },
    };
  } catch (error) {
    sendError({ error: error as Error, slug, message: 'Error on form/[slug]' });
    return {
      notFound: true,
    };
  }
};

export default FormViewPage;
